
import { Email } from '../../types';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // time to live in milliseconds
}

class EmailCacheManager {
  private folderCache = new Map<string, CacheEntry<Email[]>>();
  private emailCache = new Map<string, CacheEntry<Email>>();
  private defaultTTL = 30 * 1000; // 30 seconds for folder lists

  // Helper to create page-aware cache key (search is part of the key so
  // filtered results never collide with unfiltered pages)
  private makeKey(folder: string, limit: number, offset: number, search: string = ''): string {
    return `${folder}|${limit}|${offset}|${search}`;
  }

  getFolderCache(folder: string, limit: number, offset: number, search: string = ''): Email[] | null {
    const key = this.makeKey(folder, limit, offset, search);
    const entry = this.folderCache.get(key);
    if (entry && Date.now() - entry.timestamp < entry.ttl) {
      return entry.data;
    }
    if (entry) {
      this.folderCache.delete(key);
    }
    return null;
  }

  setFolderCache(folder: string, emails: Email[], limit: number, offset: number, searchOrTtl: string | number = '', ttl?: number): void {
    const search = typeof searchOrTtl === 'string' ? searchOrTtl : '';
    const effectiveTtl = typeof searchOrTtl === 'number' ? searchOrTtl : (ttl ?? this.defaultTTL);
    const key = this.makeKey(folder, limit, offset, search);
    this.folderCache.set(key, {
      data: emails,
      timestamp: Date.now(),
      ttl: effectiveTtl
    });
  }

  getEmailCache(emailId: string): Email | null {
    const entry = this.emailCache.get(emailId);
    if (entry && Date.now() - entry.timestamp < entry.ttl) {
      return entry.data;
    }
    this.emailCache.delete(emailId);
    return null;
  }

  setEmailCache(emailId: string, email: Email, ttl = this.defaultTTL): void {
    this.emailCache.set(emailId, {
      data: email,
      timestamp: Date.now(),
      ttl
    });
  }

  updateEmailReadStatus(emailId: string, isRead: boolean): void {
    // Update in individual email cache
    const emailEntry = this.emailCache.get(emailId);
    if (emailEntry) {
      emailEntry.data.read = isRead;
    }

    // Update in folder caches
    this.folderCache.forEach((entry) => {
      const emailIndex = entry.data.findIndex(email => email.id === emailId);
      if (emailIndex !== -1) {
        entry.data[emailIndex].read = isRead;
      }
    });
  }

  clearFolderCache(folder?: string): void {
    if (folder) {
      // Clear all page entries for this folder
      const keysToDelete: string[] = [];
      this.folderCache.forEach((_, key) => {
        if (key.startsWith(`${folder}|`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.folderCache.delete(key));
    } else {
      this.folderCache.clear();
    }
  }

  clearEmailCache(emailId?: string): void {
    if (emailId) {
      this.emailCache.delete(emailId);
    } else {
      this.emailCache.clear();
    }
  }

  clearAllCache(): void {
    this.folderCache.clear();
    this.emailCache.clear();
  }
}

export const emailCacheManager = new EmailCacheManager();
